import { DestroyRef, effect, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { FeatureFlagsEnum } from 'src/app/feature-flag-constant';
import { DeviceStore } from 'src/app/services/device.store';
import { FeatureFlagService } from 'src/app/services/feature-flag.service';
import { ProgressService } from 'src/app/services/progress.service';
import { EquipmentService } from 'src/app/state-services/equipment.service';
import { selectCompanyData } from 'src/app/stores/company/company.selector';
import { selectProfileData } from 'src/app/stores/profile/profile.selector';
import { dispatchLoadingProfileWithCompanyAndEquipment } from 'src/app/stores/store.utils';

@Injectable()
export class ResourcesPageFacade {
  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceStore = inject(DeviceStore);
  private readonly equipmentService = inject(EquipmentService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly progressService = inject(ProgressService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  readonly profile$ = this.store.select(selectProfileData);
  readonly company$ = this.store.select(selectCompanyData);
  readonly step$ = this.equipmentService.getStepAsObservable();
  readonly isAddResourceEnabled$ = this.featureFlagService.isFeatureEnabled(
    FeatureFlagsEnum.ADD_RESOURCES,
  );

  constructor() {
    this.loadLandingState();
    this.deviceStore.getCompanyDevices();
    this.advanceWhenDevicesExist();
    this.advanceWhenOnboardingIsComplete();
  }

  goToAddDevice(): void {
    this.router.navigateByUrl('/device-registration');
  }

  private loadLandingState(): void {
    dispatchLoadingProfileWithCompanyAndEquipment(this.store, {
      timeLockedProfile: true,
      includeCompany: true,
      includeMembers: false,
    });
  }

  private advanceWhenDevicesExist(): void {
    effect(() => {
      if (this.deviceStore.hasDevices()) {
        this.showResourceTables();
      }
    });
  }

  private advanceWhenOnboardingIsComplete(): void {
    this.progressService
      .getProgressAsObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((progress) => {
        if (progress >= 70 && progress <= 100) {
          this.equipmentService.setStep(2);
        }
      });
  }

  private showResourceTables(): void {
    this.equipmentService.setStep(3);
  }
}
